import React, { useContext } from 'react';
import { Builder } from '@builder.io/react';
import DefaultImage from '@/assets/images/defaultImage.jpg';
import { AuthorCard as DSAuthorCard } from '@/components/shared';
import { useAuthor } from '@/contexts/AuthorProvider';
import { useDamFile } from '@/contexts/DamFileProvider';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetTranslations } from '@/generated/translations';
import { formatTranslation } from '@/helpers';
import { MediaSet } from '@/types/media';

const fallBackImage: MediaSet = {
	size180: { url: DefaultImage },
	size320: { url: DefaultImage },
	size480: { url: DefaultImage },
	size720: { url: DefaultImage },
	size960: { url: DefaultImage },
	size1280: { url: DefaultImage },
	size1920: { url: DefaultImage },
};

/**
 * CMS AuthorCard component, showing the author referenced by the current page.
 *
 * It takes no inputs, so an editor only places the block, and the author is
 * maintained once on the page itself.
 */
export const AuthorCard: React.FunctionComponent = () => {
	const author = useAuthor();
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const damImage = useDamFile(author?.profileImage ?? null) ?? fallBackImage;

	if (!author?.name) {
		// Give the editor something to select, instead of an invisible block
		return Builder.isEditing ? <p>No author is selected on this page.</p> : null;
	}

	return (
		<DSAuthorCard
			image={damImage}
			name={author.name}
			jobTitle={author.jobTitle}
			biography={author.shortBiography}
			linkedInUrl={author.linkedInUrl}
			emailAddress={author.emailAddress}
			linkedInLabel={translations?.data.author.linkedIn || ''}
			contactLabel={translations?.data.author.contact || ''}
			linkedInAriaLabel={formatTranslation(translations?.data.author.linkedInAriaLabel || '', {
				0: author.name,
			})}
			contactAriaLabel={formatTranslation(translations?.data.author.contactAriaLabel || '', { 0: author.name })}
			lazyLoading
		/>
	);
};
